import { forwardRef, useCallback } from "react"

// --- Tiptap UI ---
import type { UseCalloutConfig } from "@/components/tiptap-ui/callout-button/use-callout"
import { useCallout } from "@/components/tiptap-ui/callout-button/use-callout"

// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor"

// --- Lib ---
import { parseShortcutKeys } from "@/lib/tiptap-utils"

// --- UI Primitives ---
import type { ButtonProps } from "@/components/tiptap-ui-primitive/button"
import { Button } from "@/components/tiptap-ui-primitive/button"
import { Badge } from "@/components/tiptap-ui-primitive/badge"

export interface CalloutButtonProps
  extends Omit<ButtonProps, "type">, UseCalloutConfig {
  text?: string
  showShortcut?: boolean
}

export const CalloutButton = forwardRef<HTMLButtonElement, CalloutButtonProps>(
  (
    {
      editor: providedEditor,
      text,
      hideWhenUnavailable = false,
      onToggled,
      showShortcut = false,
      onClick,
      children,
      ...buttonProps
    },
    ref
  ) => {
    const { editor } = useTiptapEditor(providedEditor)
    const {
      isVisible,
      canToggle,
      isActive,
      handleToggle,
      label,
      shortcutKeys,
      Icon,
    } = useCallout({ editor, hideWhenUnavailable, onToggled })

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event)
        if (event.defaultPrevented) return
        handleToggle()
      },
      [handleToggle, onClick]
    )

    if (!isVisible) return null

    return (
      <Button
        type="button"
        variant="ghost"
        data-active-state={isActive ? "on" : "off"}
        role="button"
        tabIndex={-1}
        disabled={!canToggle}
        data-disabled={!canToggle}
        aria-label={label}
        aria-pressed={isActive}
        tooltip="Callout"
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && (
              <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>
            )}
          </>
        )}
      </Button>
    )
  }
)

CalloutButton.displayName = "CalloutButton"
